import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Download, FileText, ChevronRight, Check, 
  Building2, Briefcase, Calendar, User, Mail, Loader2
} from 'lucide-react';
import { Button } from './ui/button';

// Document Template Definitions
export const DOCUMENT_TEMPLATES = [
  {
    id: 'professional-blue',
    name: 'Professional Blue',
    description: 'Corporate and clean look with blue accents',
    colors: {
      primary: '#1e40af',
      secondary: '#3b82f6',
      accent: '#dbeafe',
      text: '#1e293b',
      border: '#1e40af'
    },
    font: 'Georgia, serif'
  },
  {
    id: 'executive-red',
    name: 'Executive Red',
    description: 'Bold and impactful with red accents',
    colors: {
      primary: '#dc2626',
      secondary: '#ef4444',
      accent: '#fee2e2',
      text: '#1e293b',
      border: '#dc2626'
    },
    font: 'Palatino, serif'
  },
  {
    id: 'modern-minimal',
    name: 'Modern Minimal',
    description: 'Clean, white-space focused design',
    colors: {
      primary: '#18181b',
      secondary: '#3f3f46',
      accent: '#f4f4f5',
      text: '#27272a',
      border: '#e4e4e7'
    },
    font: 'Inter, sans-serif'
  },
  {
    id: 'classic-green',
    name: 'Classic Green',
    description: 'Traditional and formal with green accents',
    colors: {
      primary: '#166534',
      secondary: '#22c55e',
      accent: '#dcfce7',
      text: '#1e293b',
      border: '#166534'
    },
    font: 'Times New Roman, serif'
  }
];

// Template Selector Component
export function TemplateSelector({ selectedTemplate, onSelect }) {
  const templateElements = [];
  
  for (let i = 0; i < DOCUMENT_TEMPLATES.length; i++) {
    const template = DOCUMENT_TEMPLATES[i];
    const isSelected = selectedTemplate === template.id;
    
    templateElements.push(
      <button
        key={template.id}
        onClick={() => onSelect(template.id)}
        className={`relative p-4 rounded-xl border-2 transition-all text-left ${
          isSelected 
            ? 'border-red-500 bg-red-50' 
            : 'border-slate-200 hover:border-slate-300 bg-white'
        }`}
      >
        {isSelected && (
          <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
        
        {/* Template Preview */}
        <div 
          className="w-full h-24 rounded-lg mb-3 relative overflow-hidden"
          style={{ backgroundColor: template.colors.accent }}
        >
          {/* Mini document preview */}
          <div className="absolute inset-2 bg-white rounded shadow-sm">
            <div 
              className="h-1.5 m-2 rounded"
              style={{ backgroundColor: template.colors.primary, width: '60%' }}
            />
            <div 
              className="h-1 mx-2 mb-1 rounded bg-slate-200"
              style={{ width: '80%' }}
            />
            <div 
              className="h-1 mx-2 mb-1 rounded bg-slate-200"
              style={{ width: '70%' }}
            />
            <div 
              className="h-1 mx-2 rounded bg-slate-200"
              style={{ width: '75%' }}
            />
            {/* Side border accent */}
            <div 
              className="absolute left-0 top-0 bottom-0 w-1"
              style={{ backgroundColor: template.colors.border }}
            />
          </div>
        </div>
        
        <h4 className="font-semibold text-slate-900 text-sm">{template.name}</h4>
        <p className="text-xs text-slate-500 mt-1">{template.description}</p>
      </button>
    );
  }
  
  return (
    <div className="grid grid-cols-2 gap-3">
      {templateElements}
    </div>
  );
}

// Full Page Document Preview Modal
export function DocumentPreviewModal({ 
  isOpen, 
  onClose, 
  proposal, 
  sections, 
  completedSections,
  selectedTemplate,
  token,
  API_URL
}) {
  const previewRef = useRef(null);
  const [sectionsData, setSectionsData] = useState({});
  const [loading, setLoading] = useState(true);
  const template = DOCUMENT_TEMPLATES.find(t => t.id === selectedTemplate) || DOCUMENT_TEMPLATES[0];
  
  // Fetch all completed sections data when modal opens
  useEffect(() => {
    if (!isOpen || !proposal || !token) return;
    
    const fetchSections = async () => {
      setLoading(true);
      const data = {};
      
      for (let i = 0; i < completedSections.length; i++) {
        const sectionId = completedSections[i];
        try {
          const res = await fetch(
            `${API_URL}/ai/proposals/${proposal.id}/sections/${sectionId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (res.ok) {
            const sectionData = await res.json();
            if (sectionData.content) {
              data[sectionId] = sectionData;
            }
          }
        } catch (err) {
          console.error(`Failed to fetch section ${sectionId}:`, err);
        }
      }
      
      setSectionsData(data);
      setLoading(false);
    };
    
    fetchSections();
  }, [isOpen, proposal?.id, completedSections, token, API_URL]);
  
  if (!isOpen) return null;

  // Get sections with content from fetched data
  const sectionsWithContent = [];
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const sectionData = sectionsData[section.id];
    if (sectionData?.content) {
      sectionsWithContent.push({
        ...section,
        content: sectionData.content,
        citations: sectionData.citations || []
      });
    }
  }

  // Generate Table of Contents entries
  const tocEntries = [];
  for (let i = 0; i < sectionsWithContent.length; i++) {
    tocEntries.push({
      num: i + 1,
      title: sectionsWithContent[i].title,
      page: i + 3 // Start after cover and TOC
    });
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-50 overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-sm border-b border-slate-700">
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-white" />
              <span className="text-white font-medium">Document Preview</span>
              <span className="text-slate-400 text-sm">• {template.name} Template</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                onClick={() => window.print()}
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Document Preview */}
        <div className="max-w-4xl mx-auto py-8 px-4" ref={previewRef}>
          {/* Cover Page */}
          <div 
            className="bg-white shadow-2xl rounded-lg overflow-hidden mb-8"
            style={{ minHeight: '800px' }}
          >
            {/* Side Border */}
            <div className="flex min-h-[800px]">
              <div 
                className="w-3 flex-shrink-0"
                style={{ backgroundColor: template.colors.primary }}
              />
              
              <div className="flex-1 p-12 flex flex-col justify-between">
                {/* Top Section */}
                <div>
                  <div 
                    className="text-sm font-medium tracking-wider mb-2"
                    style={{ color: template.colors.secondary }}
                  >
                    BUSINESS PLAN
                  </div>
                  <h1 
                    className="text-4xl font-bold mb-4"
                    style={{ color: template.colors.primary, fontFamily: template.font }}
                  >
                    {proposal?.idea_title || 'Untitled Proposal'}
                  </h1>
                  <div className="w-24 h-1 mb-6" style={{ backgroundColor: template.colors.secondary }} />
                  <p className="text-lg text-slate-600 max-w-lg" style={{ fontFamily: template.font }}>
                    {proposal?.description || 'A comprehensive business proposal for the UK Innovator Founder Visa application.'}
                  </p>
                </div>

                {/* Bottom Section - Author Info */}
                <div className="border-t border-slate-200 pt-8 mt-8">
                  <div className="grid grid-cols-2 gap-6">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: template.colors.accent }}
                      >
                        <User className="w-5 h-5" style={{ color: template.colors.primary }} />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Prepared By</div>
                        <div className="font-medium text-slate-800">Founder Name</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: template.colors.accent }}
                      >
                        <Calendar className="w-5 h-5" style={{ color: template.colors.primary }} />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Date</div>
                        <div className="font-medium text-slate-800">
                          {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: template.colors.accent }}
                      >
                        <Briefcase className="w-5 h-5" style={{ color: template.colors.primary }} />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Category</div>
                        <div className="font-medium text-slate-800">{proposal?.category || 'Technology'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: template.colors.accent }}
                      >
                        <Building2 className="w-5 h-5" style={{ color: template.colors.primary }} />
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Location</div>
                        <div className="font-medium text-slate-800">United Kingdom</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents Page */}
          <div 
            className="bg-white shadow-2xl rounded-lg overflow-hidden mb-8"
            style={{ minHeight: '600px' }}
          >
            <div className="flex min-h-[600px]">
              <div 
                className="w-3 flex-shrink-0"
                style={{ backgroundColor: template.colors.primary }}
              />
              
              <div className="flex-1 p-12">
                <h2 
                  className="text-2xl font-bold mb-8"
                  style={{ color: template.colors.primary, fontFamily: template.font }}
                >
                  Table of Contents
                </h2>
                
                <div className="space-y-3">
                  {(() => {
                    const items = [];
                    for (let i = 0; i < tocEntries.length; i++) {
                      const entry = tocEntries[i];
                      items.push(
                        <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-100">
                          <span 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium"
                            style={{ backgroundColor: template.colors.accent, color: template.colors.primary }}
                          >
                            {entry.num}
                          </span>
                          <span className="flex-1 text-slate-800" style={{ fontFamily: template.font }}>
                            {entry.title}
                          </span>
                          <span className="text-slate-400 text-sm">Page {entry.page}</span>
                        </div>
                      );
                    }
                    return items;
                  })()}
                </div>

                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-12 h-12 mx-auto mb-3 animate-spin text-slate-400" />
                    <p className="text-slate-500">Loading sections...</p>
                  </div>
                ) : sectionsWithContent.length === 0 ? (
                  <div className="text-center py-12 text-slate-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No sections completed yet.</p>
                    <p className="text-sm mt-1">Complete and approve sections to see them here.</p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Content Pages */}
          {(() => {
            const pages = [];
            for (let i = 0; i < sectionsWithContent.length; i++) {
              const section = sectionsWithContent[i];
              pages.push(
                <div 
                  key={section.id}
                  className="bg-white shadow-2xl rounded-lg overflow-hidden mb-8"
                  style={{ minHeight: '600px' }}
                >
                  <div className="flex min-h-[600px]">
                    <div 
                      className="w-3 flex-shrink-0"
                      style={{ backgroundColor: template.colors.primary }}
                    />
                    
                    <div className="flex-1 p-12">
                      {/* Section Header */}
                      <div className="mb-8">
                        <div 
                          className="text-sm font-medium tracking-wider mb-2"
                          style={{ color: template.colors.secondary }}
                        >
                          SECTION {i + 1}
                        </div>
                        <h2 
                          className="text-2xl font-bold"
                          style={{ color: template.colors.primary, fontFamily: template.font }}
                        >
                          {section.title}
                        </h2>
                        <div 
                          className="w-16 h-1 mt-3"
                          style={{ backgroundColor: template.colors.secondary }}
                        />
                      </div>

                      {/* Section Content */}
                      <div 
                        className="prose prose-slate max-w-none"
                        style={{ fontFamily: template.font, color: template.colors.text }}
                        dangerouslySetInnerHTML={{ 
                          __html: section.content.replace(/\n/g, '<br/>') 
                        }}
                      />

                      {/* Citations */}
                      {section.citations && section.citations.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-slate-200">
                          <h4 
                            className="text-sm font-semibold mb-3"
                            style={{ color: template.colors.primary }}
                          >
                            References
                          </h4>
                          <div className="space-y-1">
                            {(() => {
                              const cites = [];
                              for (let j = 0; j < section.citations.length; j++) {
                                cites.push(
                                  <p key={j} className="text-sm text-slate-600">
                                    [{j + 1}] {section.citations[j]}
                                  </p>
                                );
                              }
                              return cites;
                            })()}
                          </div>
                        </div>
                      )}

                      {/* Page Footer */}
                      <div className="absolute bottom-6 right-12 text-sm text-slate-400">
                        Page {i + 3}
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            return pages;
          })()}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default DocumentPreviewModal;
